import React, { useEffect, useMemo, useState } from "react";

import Button from "../../../Components/Button";
import CommonModal from "../../../Components/Modal";

function vendorPersonId(person) {
  return String(
    person?.user_id || person?.id || person?.executive_id || ""
  ).trim();
}

function vendorPersonName(person) {
  return (
    person?.full_name ||
    person?.name ||
    person?.member_name ||
    person?.user_name ||
    ""
  ).trim();
}

function normalizeTransferCandidates(candidates) {
  const seen = new Set();
  const out = [];
  for (const person of candidates || []) {
    const id = vendorPersonId(person);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      user_id: id,
      full_name: vendorPersonName(person) || id,
    });
  }
  return out;
}

/** contact_id → vendor user_ids who still cover that contact after this save. */
function ownersByContactId(remainingCoverageLinks) {
  const map = new Map();
  for (const link of remainingCoverageLinks || []) {
    const contactId = String(link?.contact_id || "").trim();
    const subjectId = String(link?.subject_id || "").trim();
    if (!contactId || !subjectId) continue;
    if (!map.has(contactId)) map.set(contactId, new Set());
    map.get(contactId).add(subjectId);
  }
  return map;
}

export function MappingReviewModal({
  open,
  preview,
  newLinkCandidates,
  vendorLookup,
  remainingCoverageLinks,
  nested = false,
  onClose,
  onConfirm,
}) {
  const atRisk = useMemo(
    () => (preview?.removed_pairs || []).filter(p => p.at_risk),
    [preview]
  );

  const nameByVendorId = useMemo(() => {
    const map = new Map();
    const people = [...(vendorLookup || []), ...(newLinkCandidates || [])];
    for (const person of people) {
      const id = vendorPersonId(person);
      const name = vendorPersonName(person);
      if (id && name && !map.has(id)) map.set(id, name);
    }
    return map;
  }, [vendorLookup, newLinkCandidates]);

  const transferCandidates = useMemo(
    () => normalizeTransferCandidates(newLinkCandidates),
    [newLinkCandidates]
  );

  const coverageByContact = useMemo(
    () => ownersByContactId(remainingCoverageLinks),
    [remainingCoverageLinks]
  );

  const [decisions, setDecisions] = useState({});

  useEffect(() => {
    if (open) setDecisions({});
  }, [open, preview]);

  if (!open) return null;

  const handleConfirm = () => {
    const transfer_decisions = atRisk.map(pair => ({
      relationship_id: pair.relationship_id,
      successor_user_id: decisions[pair.relationship_id] || null,
      leave_dormant: !decisions[pair.relationship_id],
    }));
    onConfirm(transfer_decisions);
  };

  return (
    <CommonModal
      isOpen={open}
      toggle={onClose}
      title="Review coverage changes"
      size="lg"
      usePortal
      nested={nested}
    >
      <p className="mb-3 text-sm text-gray-600">
        These removed relationships have RL or goal data. Transfer each pair to
        another vendor owner on this account who does not already cover that
        contact, or leave the pair dormant.
      </p>
      <p
        className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
        role="note"
      >
        <span className="font-semibold">Leave pair dormant</span> parks this
        vendor↔contact mapping only. The person{" "}
        <span className="font-semibold">stays on the account team</span> and
        will <span className="font-semibold">not</span> appear on Departed Row.
        To remove them from the account and open Departed Row holes, use Team
        Views → Reassign / Remove (or Teams → Unlink).
      </p>
      <div className="space-y-3">
        {atRisk.map(pair => {
          const subjectId = String(pair.subject_id || "").trim();
          const contactId = String(pair.contact_id || "").trim();
          const vendorName =
            nameByVendorId.get(subjectId) || subjectId || "Vendor";
          const contactName = pair.contact_name || pair.contact_id || "Contact";
          const rlLabel =
            pair.latest_rl != null ? ` — RL ${pair.latest_rl}` : "";
          const alreadyCovering = coverageByContact.get(contactId) || new Set();
          const candidatesForPair = transferCandidates.filter(c => {
            if (c.user_id === subjectId) return false;
            // Same rule as Reassign / warm handoff: successor must not
            // already cover this contact in the map being saved.
            if (alreadyCovering.has(c.user_id)) return false;
            return true;
          });

          return (
            <div key={pair.relationship_id} className="rounded border p-3">
              <div className="font-medium text-gray-900">
                {vendorName}
                <span className="mx-1.5 text-gray-400" aria-hidden>
                  →
                </span>
                {contactName}
                {rlLabel}
              </div>
              <select
                className="mt-2 w-full rounded border border-gray-200 px-2 py-1 text-sm"
                value={decisions[pair.relationship_id] || ""}
                onChange={e =>
                  setDecisions(prev => ({
                    ...prev,
                    [pair.relationship_id]: e.target.value,
                  }))
                }
              >
                <option value="">
                  Leave pair dormant (person stays on team)
                </option>
                {candidatesForPair.map(c => (
                  <option key={c.user_id} value={c.user_id}>
                    Transfer to {c.full_name}
                  </option>
                ))}
              </select>
              {!candidatesForPair.length ? (
                <p className="mt-1.5 text-xs text-gray-500">
                  No other vendor on this account can take this pair without
                  already covering {contactName}. Leave pair dormant or unmap
                  their existing link first.
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleConfirm}>Confirm save</Button>
      </div>
    </CommonModal>
  );
}

export default MappingReviewModal;
